import type { User } from "firebase/auth";
import type { PrivacyMode, ProfileUser } from "../types/profile";
import type { DateValue } from "../types/data";

interface PrivacyProfile extends ProfileUser { username?: string; publicProfileEnabled?: boolean; }

export const PROFILE_PRIVACY_MODES = Object.freeze({
  public: "public",
  friends: "friends",
  private: "private"
});

export const DEFAULT_PROFILE_PRIVACY_MODE = PROFILE_PRIVACY_MODES.public;
export const PUBLIC_PROFILES_COLLECTION = "publicProfiles";
export const SITE_ADMIN_UID = "SsnjLmgBFnV9lZyQNGNKxLhYgi32";
const PENDING_PRIVACY_KEY_PREFIX = "cineparagon_pending_profile_privacy:";

const SUPPORTED_PRIVACY_MODES = new Set<string>(Object.values(PROFILE_PRIVACY_MODES));
const isPrivacyMode = (value: string): value is PrivacyMode => SUPPORTED_PRIVACY_MODES.has(value);

export const normalizeProfilePrivacyMode = (value: unknown, { isDemo = false }: { isDemo?: boolean } = {}): PrivacyMode => {
  if (isDemo) return PROFILE_PRIVACY_MODES.public;
  const normalized = String(value || "").trim().toLowerCase();
  return isPrivacyMode(normalized)
    ? normalized
    : DEFAULT_PROFILE_PRIVACY_MODE;
};

export const getProfilePrivacyMode = (profile: PrivacyProfile | null = {}): PrivacyMode => {
  if (profile?.isDemo === true) return PROFILE_PRIVACY_MODES.public;
  const explicitMode = String(profile?.privacyMode || "").trim().toLowerCase();
  if (isPrivacyMode(explicitMode)) return explicitMode;
  // Before privacyMode existed, publicProfileEnabled was the server-enforced
  // visibility switch. Preserve an old opt-out instead of treating it as a
  // public profile during the rules migration.
  if (profile?.publicProfileEnabled === false) return PROFILE_PRIVACY_MODES.private;
  return DEFAULT_PROFILE_PRIVACY_MODE;
};

export const getPendingProfilePrivacyMode = (uid: string|undefined) => {
  if (!uid || typeof localStorage === "undefined") return null;
  try {
    const value = String(localStorage.getItem(`${PENDING_PRIVACY_KEY_PREFIX}${uid}`) || "");
    return isPrivacyMode(value) ? value : null;
  } catch {
    return null;
  }
};

export const setPendingProfilePrivacyMode = (uid: string, mode: string) => {
  if (!uid || typeof localStorage === "undefined") return;
  try {
    const normalized = String(mode || "").trim().toLowerCase();
    if (SUPPORTED_PRIVACY_MODES.has(normalized)) {
      localStorage.setItem(`${PENDING_PRIVACY_KEY_PREFIX}${uid}`, normalized);
    }
  } catch {}
};

export const clearPendingProfilePrivacyMode = (uid: string) => {
  if (!uid || typeof localStorage === "undefined") return;
  try {
    localStorage.removeItem(`${PENDING_PRIVACY_KEY_PREFIX}${uid}`);
  } catch {}
};

export const isAdministratorIdentity = (user: Pick<User, "uid"> | null | undefined, claims: { admin?: unknown } = {}) => Boolean(
  user?.uid === SITE_ADMIN_UID ||
  claims?.admin === true
);

const optionalDisplayString = (value: unknown) => {
  const normalized = String(value || "").trim();
  return normalized || "";
};

export const buildPublicProfileDocument = ({
  uid,
  profile = {},
  authUser = null,
  updatedAt = null
}: { uid?: string; profile?: PrivacyProfile; authUser?: Pick<User, "uid" | "displayName" | "email" | "photoURL"> | null; updatedAt?: DateValue } = {}) => {
  const isDemo = profile?.isDemo === true;
  const name = optionalDisplayString(
    profile?.name ||
    profile?.displayName ||
    authUser?.displayName ||
    authUser?.email?.split?.("@")?.[0] ||
    "Пользователь"
  );
  const displayName = optionalDisplayString(profile?.displayName || name);
  const avatarUrl = optionalDisplayString(
    profile?.avatarUrl || profile?.photoURL || authUser?.photoURL
  );

  return {
    uid: optionalDisplayString(uid || authUser?.uid),
    name,
    nameLower: name.toLowerCase(),
    displayName,
    username: optionalDisplayString(profile?.username),
    avatarUrl,
    photoURL: avatarUrl,
    privacyMode: getProfilePrivacyMode({ ...profile, isDemo }),
    isDemo,
    ...(updatedAt ? { updatedAt } : {})
  };
};

export const getPrivacyAccessMessage = (mode: unknown) => (
  normalizeProfilePrivacyMode(mode) === PROFILE_PRIVACY_MODES.friends
    ? {
      title: "Профиль доступен только друзьям",
      description: "Добавьте пользователя в друзья, чтобы получить доступ к его профилю."
    }
    : {
      title: "Этот профиль закрыт",
      description: "Пользователь ограничил доступ к своим оценкам, статистике и активности."
    }
);

export const getOwnerPrivacyNotice = (mode: string) => {
  const normalized = normalizeProfilePrivacyMode(mode);
  if (normalized === PROFILE_PRIVACY_MODES.friends) {
    return "Ваш профиль доступен только подтверждённым друзьям.";
  }
  if (normalized === PROFILE_PRIVACY_MODES.private) {
    return "Ваш профиль закрыт. Другие пользователи не видят ваши оценки, статистику и активность.";
  }
  return "";
};

export const canViewProfileFromState = ({
  viewerId,
  ownerId,
  privacyMode,
  isAdmin = false,
  isConfirmedFriend = false,
  isDemo = false
}: { viewerId?: string | null; ownerId?: string | null; privacyMode?: PrivacyMode; isAdmin?: boolean; isConfirmedFriend?: boolean; isDemo?: boolean } = {}) => {
  if (viewerId && viewerId === ownerId) return true;
  if (isAdmin || isDemo) return true;
  const normalized = normalizeProfilePrivacyMode(privacyMode);
  if (normalized === PROFILE_PRIVACY_MODES.public) return true;
  if (normalized === PROFILE_PRIVACY_MODES.friends) return isConfirmedFriend;
  return false;
};
